import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { BusinessOpportunity } from '../types';
import { auth, db } from '../lib/firebase';
import { COLLECTION_BUSINESS_OPPORTUNITIES } from '../lib/firestorePaths';
import { normalizeBusinessOpportunity } from '../utils/businessOpportunity';

export type BusinessOpportunitiesState = {
  businessOpportunities: BusinessOpportunity[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: BusinessOpportunitiesState = {
  businessOpportunities: [],
  loading: false,
  error: null,
};

function docToBusinessOpportunity(d: QueryDocumentSnapshot): BusinessOpportunity {
  return normalizeBusinessOpportunity(d.data() as Record<string, unknown>, d.id);
}

export function useBusinessOpportunitiesState(userId: string): BusinessOpportunitiesState {
  const [businessOpportunities, setBusinessOpportunities] = useState<BusinessOpportunity[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ownerId = auth.currentUser?.uid?.trim() || userId.trim();
    if (!ownerId) {
      setBusinessOpportunities([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const qItems = query(
      collection(db, COLLECTION_BUSINESS_OPPORTUNITIES),
      where('userId', '==', ownerId)
    );

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const itemsSnap = await getDocs(qItems);
        if (cancelled) return;

        setBusinessOpportunities(itemsSnap.docs.map(docToBusinessOpportunity));

        unsubscribers.push(
          onSnapshot(
            qItems,
            (snap) => setBusinessOpportunities(snap.docs.map(docToBusinessOpportunity)),
            (e) => setError(e.message)
          )
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load business opportunities');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId]);

  return useMemo(
    () => ({ businessOpportunities, loading, error }),
    [businessOpportunities, loading, error]
  );
}

export function emptyBusinessOpportunitiesState(): BusinessOpportunitiesState {
  return EMPTY_STATE;
}
